/**
 * Shared detectors for Auth0 errors that require re-authentication.
 *
 * Background (production incident — Auth0 refresh-token migration, PR #189):
 * we moved Auth0 to the custom API audience `https://api.rettx.eu` with
 * `useRefreshTokens: true` and `useRefreshTokensFallback: false`. The fallback
 * MUST stay false (it prevents a 60s hidden-iframe hang on the native Capacitor
 * app). A side effect is that any user whose cached localStorage session predates
 * the migration has NO refresh token for the new audience, so the SDK's silent
 * `getAccessTokenSilently()` call throws auth0-spa-js `MissingRefreshTokenError`
 * (`.error === 'missing_refresh_token'`, message
 * `Missing Refresh Token (audience: 'https://api.rettx.eu', scope: ...)`).
 *
 * Two DISTINCT situations, with DIFFERENT recovery actions (PR after #190):
 *
 *  - `login_required` — the user is UNAUTHENTICATED (anonymous visitor, or a
 *    fully expired session). Recovery = navigate to the `/login` route, which
 *    forwards to the public landing page (`/`). We must NOT force
 *    `loginWithRedirect()` here or anonymous visitors would be dragged into
 *    Auth0 and could never browse the public landing page.
 *
 *  - `missing_refresh_token` — there IS a cached session (so
 *    `isAuthenticated$ === true`) but no refresh token for the new audience: a
 *    BROKEN-but-authenticated state. #190 routed this to the `/login` route too,
 *    which caused an INFINITE REDIRECT LOOP: `/login`
 *    (`LegacyLoginRedirectComponent`) → `/` → `publicOnlyGuard` sees the user as
 *    authenticated → bounces to `/patients` → resolver re-throws
 *    `missing_refresh_token` → `/login` → … Nothing ever called
 *    `loginWithRedirect()`, so the stale session was never replaced. Recovery
 *    MUST be a real interactive `loginWithRedirect()` (full-page authorize) that
 *    unloads the SPA, breaks the loop, and mints a fresh token WITH a refresh
 *    token on return — see `initiateInteractiveReauth`.
 *
 *  - `invalid_grant` / "Unknown or invalid refresh token" — there IS a cached
 *    session (restored, so `isAuthenticated$ === true`) AND a refresh token, but
 *    that rotating refresh token is EXPIRED/REVOKED/already-rotated, so Auth0's
 *    `POST /oauth/token` grant is rejected (`invalid_grant`, description
 *    `Unknown or invalid refresh token.`). This is the same BROKEN-but-
 *    authenticated state as `missing_refresh_token`: the user was let through the
 *    guard, then the silent token acquisition fails. Recovery is IDENTICAL — a
 *    full-page interactive `loginWithRedirect()` (see `initiateInteractiveReauth`).
 *    Routing it to `/login` would loop exactly like the #190 regression above.
 *    Both families are unified under `isInteractiveReauthRequiredError`.
 *
 * These are pure functions with no Angular dependencies, so the matching logic
 * — and its unit test — live once.
 */

type ErrorRecord = Record<string, unknown>;

export type UnmatchedAuthErrorSignal =
  | 'auth0-standard-error'
  | 'refresh-token-reference';

/**
 * Auth0 errors are rethrown unchanged by auth0-angular, but Zone.js and
 * HttpErrorResponse can wrap them under `rejection` and `error`, respectively.
 * Inspect only the direct error, either wrapper, and their proven composition.
 * Do not recurse into arbitrary API payloads: a domain response may itself have
 * an `error` property whose value happens to resemble an Auth0 code.
 */
function errorRecords(error: unknown): ErrorRecord[] {
  if (!error || typeof error !== 'object') {
    return [];
  }

  const direct = error as ErrorRecord;
  const rejection = asErrorRecord(direct['rejection']);
  const httpBody = asErrorRecord(direct['error']);
  const rejectedHttpBody = asErrorRecord(rejection?.['error']);

  return [direct, rejection, httpBody, rejectedHttpBody].filter(
    (record): record is ErrorRecord => record !== null,
  );
}

function asErrorRecord(value: unknown): ErrorRecord | null {
  return value && typeof value === 'object'
    ? value as ErrorRecord
    : null;
}

/** Collects the structured Auth0 error-code candidates across known wrappers. */
function errorCodeCandidates(error: unknown): unknown[] {
  return errorRecords(error).map((record) => record['error']);
}

/** Collects Auth0 descriptions and Error messages across known wrappers. */
function errorMessageCandidates(error: unknown): unknown[] {
  return errorRecords(error).flatMap((record) => [
    record['message'],
    record['error_description'],
  ]);
}

function matchesCode(error: unknown, code: string): boolean {
  return errorCodeCandidates(error).some(
    (candidate) => candidate === code,
  );
}

function matchesMessageFragment(error: unknown, fragment: string): boolean {
  return errorMessageCandidates(error).some(
    (message) =>
      typeof message === 'string' &&
      message.toLowerCase().includes(fragment),
  );
}

/**
 * Matches ONLY the `login_required` family (user is unauthenticated). Recovery
 * for this case stays a navigation to the `/login` route so anonymous visitors
 * can still reach the public landing page.
 */
export function isLoginRequiredError(error: unknown): boolean {
  return (
    matchesCode(error, 'login_required') ||
    matchesMessageFragment(error, 'login_required') ||
    matchesMessageFragment(error, 'login required')
  );
}

/**
 * Matches ONLY the `missing_refresh_token` family (a cached-but-tokenless,
 * still-authenticated session). Recovery for this case MUST be a real
 * `loginWithRedirect()` (see `initiateInteractiveReauth`) — NOT a navigation to
 * `/login`, which loops via `LegacyLoginRedirectComponent` + `publicOnlyGuard`.
 */
export function isMissingRefreshTokenError(error: unknown): boolean {
  return (
    matchesCode(error, 'missing_refresh_token') ||
    matchesMessageFragment(error, 'missing_refresh_token') ||
    matchesMessageFragment(error, 'missing refresh token')
  );
}

/**
 * Matches ONLY the invalid/expired-refresh-token family: Auth0 rejects the
 * silent `POST /oauth/token` refresh grant with `invalid_grant` and description
 * `Unknown or invalid refresh token.` when the stored rotating refresh token is
 * expired, revoked, or already rotated. Like `missing_refresh_token` this is a
 * BROKEN-but-authenticated session (the SDK still reports the restored session
 * as authenticated), so recovery MUST be a full-page `loginWithRedirect()` (see
 * `initiateInteractiveReauth`) — NOT a navigation to `/login`, which loops.
 *
 * Matches both the structured code (`invalid_grant`) and the human-readable
 * message across the wrapper shapes we see (direct, promise `rejection`, nested
 * HttpErrorResponse `error` body), case-insensitively.
 */
export function isInvalidRefreshTokenError(error: unknown): boolean {
  return (
    matchesCode(error, 'invalid_grant') ||
    matchesMessageFragment(error, 'unknown or invalid refresh token') ||
    matchesMessageFragment(error, 'invalid refresh token')
  );
}

/**
 * True when `error` is a BROKEN-but-authenticated session that must be recovered
 * by a full-page interactive `loginWithRedirect()` (see
 * `initiateInteractiveReauth`) rather than a navigation to `/login`. This unifies
 * the two families with the SAME recovery action: `missing_refresh_token` (no
 * refresh token for the new audience — stale pre-migration cache) and
 * `invalid_grant` / "Unknown or invalid refresh token" (the refresh token exists
 * but is expired/revoked/rotated). Consumers branch on this for the interactive-
 * reauth path, keeping the distinct `isLoginRequiredError` → `/login` path intact.
 */
export function isInteractiveReauthRequiredError(error: unknown): boolean {
  return isMissingRefreshTokenError(error) || isInvalidRefreshTokenError(error);
}

/**
 * True when `error` is any Auth0 error that should be recovered by
 * re-authentication (either family). Kept for the call sites that only need to
 * know "is this a re-auth error I should swallow?"; the specific recovery ACTION
 * is chosen by branching on `isInteractiveReauthRequiredError` (full-page
 * `loginWithRedirect`) vs `isLoginRequiredError` (navigate to `/login`).
 */
export function isReauthRequiredError(error: unknown): boolean {
  return isLoginRequiredError(error) || isInteractiveReauthRequiredError(error);
}

/**
 * Returns an identifier-free signal for auth-shaped errors that the recovery
 * classifier intentionally did not match. The original exception remains the
 * diagnostic source; this closed value only makes classifier misses queryable.
 */
export function getUnmatchedAuthErrorSignal(
  error: unknown,
): UnmatchedAuthErrorSignal | null {
  if (isReauthRequiredError(error)) {
    return null;
  }

  const records = errorRecords(error);
  if (
    records.some(
      (record) =>
        typeof record['error'] === 'string' &&
        typeof record['error_description'] === 'string',
    )
  ) {
    return 'auth0-standard-error';
  }

  if (matchesMessageFragment(error, 'refresh token')) {
    return 'refresh-token-reference';
  }

  return null;
}
